// @file Composable for reading clipboard items with error handling and snackbar feedback
import { __ } from '@@/bits/intl'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { useSurfaceStore } from '@@/pinia/surface'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { storeToRefs } from 'pinia'
import type { TLPageId } from 'tldraw'

function useClipboardItems(): {
  readClipboardItems: () => Promise<ClipboardItems | null>
  getClipboardFrameId: (clipboardItems: ClipboardItems) => Promise<TLPageId | null>
  writeClipboardFrameId: (frameId: TLPageId) => Promise<boolean>
} {
  const surfaceStore = useSurfaceStore()
  const whiteboardStore = useWhiteboardStore()

  async function readClipboardItems(): Promise<ClipboardItems | null> {
    try {
      if (typeof navigator.clipboard?.read !== 'function') {
        throw new Error('Clipboard API not supported')
      }
      return await navigator.clipboard.read()
    } catch (error: unknown) {
      if (!(error instanceof Error)) {
        return null
      }

      const [{ useGlobalSnackbarStore }, { SnackbarNotificationType }] = await Promise.all([
        import('@@/pinia/global_snackbar'),
        import('@@/enums'),
      ])
      const globalSnackbarStore = useGlobalSnackbarStore()

      // Error case for permission denied
      // Note: error.messages are localised so we have to use `error.name`
      if (error.name === 'NotAllowedError') {
        globalSnackbarStore.setSnackbar({
          message: __('Clipboard access denied—please enable clipboard permissions in your browser to allow pasting.'),
          notificationType: SnackbarNotificationType.error,
          timeout: 3000,
        })
      } else if (error.name === 'Error' && error.message === 'Clipboard API not supported') {
        // As "the browser not having support for clipboard" does not have an actual error name, we use the "Clipboard API not supported" message above as the identifier
        globalSnackbarStore.setSnackbar({
          message: __('Pasting from the clipboard is not supported in your browser.'),
          notificationType: SnackbarNotificationType.error,
          timeout: 3000,
        })
      } else {
        // Generic error , just in case
        globalSnackbarStore.setSnackbar({
          message: __('Unable to access clipboard.'),
          notificationType: SnackbarNotificationType.error,
          timeout: 3000,
        })
      }

      return null
    }
  }

  /**
   * Reads the clipboard and attempts to extract a Padlet frame ID if present.
   *
   * This function follows a similar pattern to TLDraw's clipboard handling:
   * @see https://github.com/tldraw/tldraw/blob/main/packages/tldraw/src/lib/ui/hooks/useClipboardEvents.ts#L603
   *
   * The function:
   * 1. Takes pre-read clipboard items as input (avoiding additional clipboard reads)
   * 2. Iterates through each clipboard item looking for HTML format
   * 3. Extracts base64-encoded Padlet frame data from hidden HTML div
   * 4. Attempts to parse the decoded data as JSON to find Padlet frame data
   * 5. Returns the frameId if found, regardless of which sandbox it came from
   *
   * Note: This allows cross-sandbox clipboard reading. Ownership validation and frame
   * availability will be checked during the paste operation.
   *
   * @param clipboardItems - Pre-read clipboard items (avoids Safari's one-read-per-gesture limit)
   * @returns Promise<TLPageId | null> The frame ID if found in clipboard, null otherwise
   */
  async function getClipboardFrameId(clipboardItems: ClipboardItems): Promise<TLPageId | null> {
    try {
      for (const item of clipboardItems) {
        if (item.types.includes('text/html')) {
          const htmlBlob = await item.getType('text/html')
          const html = await htmlBlob.text()

          const match = html.match(/data-padlet-frame="([^"]*)"/)
          if (match) {
            try {
              const decodedData = atob(match[1])
              const clipboardData = JSON.parse(decodedData)
              if (
                clipboardData.type === 'application/padlet-whiteboard' &&
                clipboardData.kind === 'frame' &&
                typeof clipboardData.data?.frameId === 'string'
              ) {
                return clipboardData.data.frameId as TLPageId
              }
            } catch (parseError) {
              // Not our format, continue to next item
            }
          }
        }
      }

      return null
    } catch (error) {
      // Silently fail for frame detection - we don't want to show errors for normal clipboard content
      return null
    }
  }

  /**
   * Writes a frame ID to the clipboard in both HTML and plain text formats.
   *
   * - **HTML format**: Hidden metadata for internal Padlet use
   * - **Plain text format**: Human-readable description (e.g., "Padlet card from 'My Board' (Card #3)")
   */
  async function writeClipboardFrameId(frameId: TLPageId): Promise<boolean> {
    const frameIndex = whiteboardStore.getFrameIndex(frameId) + 1
    const { title } = storeToRefs(surfaceStore)
    try {
      // Create Padlet clipboard data similar to TLDraw's format
      const clipboardData = {
        type: 'application/padlet-whiteboard',
        kind: 'frame',
        version: 1,
        data: {
          frameId,
        },
      }

      const jsonString = asciiSafeStringify(clipboardData)

      // This "code" format is only for internal use. Most applications will ignore the HTML as they do not have a paste handler to handle "data-padlet-frame."
      const htmlData = `<code data-padlet-frame="${btoa(jsonString)}"></code>`

      // Pasting in the sandbox doesn't use `text/plain` format, so we are free to
      // put anything in the text/plain format.
      // What we did here is to put the title and frame index so it will be an identifiable text (not a blob or frameID) when users pastes this to a text editor.
      const textData = __('Padlet card from "%{sandboxTitle}" (Card number %{cardNumber})', {
        sandboxTitle: title.value,
        cardNumber: frameIndex,
      })

      // Write both formats to clipboard
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlData], { type: 'text/html' }),
        'text/plain': new Blob([textData], { type: 'text/plain' }),
      })

      await navigator.clipboard.write([clipboardItem])

      return true
    } catch (error) {
      return false
    }
  }

  return { readClipboardItems, getClipboardFrameId, writeClipboardFrameId }
}

export { useClipboardItems }
